/***
*
*   AUTHENTICATION
*   Auth provider to manage auth functions throughout
*   the application. <PrivateRoute> component to
*   protect internal application routes from unauthenticated
*   access.
*
**********/

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Navigate } from 'react-router-dom';
import { Event } from 'components/event';
import { mensajeAdvertencia } from 'components/mensajes';

// auth context
export const AuthContext = React.createContext();

const permissions = require('./permissions');

export function AuthProvider(props){

  const cache = JSON.parse(localStorage.getItem('user'));
  const [user, setUser] = useState(cache);
  const [auth, setAuth] = useState({ data: null, loading: Boolean(cache) });
  const token = user?.token;

  // Aviso de sesión expirada: el interceptor de axios (app.js) deja esta marca
  // antes de sacar al login; se muestra aquí al cargar el destino para que el
  // mensaje persista tras el redirect.
  useEffect(() => {
    if (sessionStorage.getItem('sesion_expirada')) {
      sessionStorage.removeItem('sesion_expirada');
      mensajeAdvertencia('Por seguridad, vuelve a iniciar sesión.', 'Tu sesión ha expirado');
      return;
    }

    if (sessionStorage.getItem('sin_acceso_panel')) {
      sessionStorage.removeItem('sin_acceso_panel');
      mensajeAdvertencia(
        'Esta cuenta no tiene acceso al panel administrativo. Ingresa desde la aplicación móvil de Tu Ruta Segura.',
        'No tienes permiso'
      );
    }
  }, []);

  useEffect(() => {
    if (!token) {
      setAuth({ data: null, loading: false });
      return;
    }

    let mounted = true;

    setAuth(current => ({ ...current, loading: true }));
    axios({ method: 'get', url: '/api/auth' })
      .then(res => {
        if (mounted) setAuth({ data: res.data.data, loading: false });
      })
      .catch(err => {
        if (!mounted) return;

        // Solo un 401 significa que el token dejó de servir. Un 500, un fallo
        // de red o un timeout no son motivo para expulsar al usuario: antes
        // cualquier error caía aquí y se le decía "tu sesión ha expirado",
        // que ocultaba el error real del servidor.
        if (err?.response?.status === 401)
          setAuth({ data: { authenticated: false }, loading: false });
        else
          setAuth({ data: null, loading: false });
      });

    return () => { mounted = false; };
  }, [token]);

  useEffect(() => {

    // update the auth status
    if (!auth.loading && auth.data){

      // Si el servidor ya no reconoce el token, la sesión venció por su cuenta.
      auth.data.authenticated ?
        update(auth.data) : signout(true);

    }
  }, [auth]);

  function signin(res){

    if (res.data){

      localStorage.setItem('user', JSON.stringify(res.data));
      axios.defaults.headers.common['Authorization'] = 'Bearer ' + res.data.token;
      Event('signin');

      if (res.data.must_change_password)
        return window.location = '/account/password';

      // Coordinadores y contabilidad no requieren plan ni suscripción
      if (res.data.permission === 'coordinator')
        return window.location = '/dashboard';

      if (res.data.permission === 'accounting')
        return window.location = '/accounting/dashboard';

      if (!res.data.plan)
        return window.location = '/signup/plan';

      // Redirigir a la URL guardada (ej. desde click en notificación push)
      const savedRedirect = sessionStorage.getItem('redirect_after_login');
      if (savedRedirect) {
        sessionStorage.removeItem('redirect_after_login');
        return window.location = savedRedirect;
      }

      return window.location = res.data.onboarded ? '/dashboard' : '/welcome';

    }
  }

  /* signout(expirada)
   * expirada = true cuando la sesión se cerró sola (inactividad o token
   * inválido): deja la marca para que el login muestre el aviso.
   * Sin argumento es un cierre voluntario y sale en silencio. */
  async function signout(expirada){

    if (expirada === true)
      sessionStorage.setItem('sesion_expirada', '1');

    axios({ method: 'delete', url: '/api/auth' });
    localStorage.clear();
    return window.location = '/'

  }

  async function switchAccount(id){

    const res = await axios({ 
      
      method: 'post', 
      url: '/api/auth/switch',
      data: { account: id }
    
    });

    if (res.data)
      signin(res)

  }

  function update(data){

    if (localStorage.getItem('user')){

      let user = JSON.parse(localStorage.getItem('user'));
      for (let key in data){

        if (Array.isArray(data[key])){
       
          user[key] = data[key]

        }
        else if (typeof data[key] === 'object'){
          for (let innerKey in data[key]){

            user[key][innerKey] = data[key][innerKey]

          }
        }
        else {
          
          user[key] = data[key];

        }
      }

      localStorage.setItem('user', JSON.stringify(user));
      setUser(user);

    }
  }

  return (
    <AuthContext.Provider value={{

      user: user,
      signin: signin,
      signout: signout,
      update: update,
      switchAccount: switchAccount,
      permission: permissions[user?.permission]

    }}

    {...props} />
  );
}

// Roles que pueden usar el panel web. Conductores, azafatas, conductores
// secundarios y clientes usan la app móvil. El backend ya bloquea el login,
// esto corta las sesiones que quedaron abiertas antes de ese cambio.
const WEB_PANEL_PERMISSIONS = ['master', 'owner', 'admin', 'coordinator', 'accounting'];

// custom route object checks for an auth token before
// rendering the route – redirects if token is not present
export function PrivateRoute(props){

  // check user exists
  const user = JSON.parse(localStorage.getItem('user'));
  const path = window.location.pathname;

  if (user?.token && !WEB_PANEL_PERMISSIONS.includes(user.permission)){

    localStorage.clear();
    sessionStorage.setItem('sin_acceso_panel', '1');
    return <Navigate to='/' />;

  }

  if (user?.token){
    if (permissions[user.permission][props.permission]){

      // coordinadores y contabilidad no usan plan ni suscripción
      if (user.permission !== 'coordinator' && user.permission !== 'accounting') {

        // user has no plan
        if (!user.plan && path !== '/account/profile' && path !== '/signup/plan')
          return <Navigate to='/signup/plan'/>;

        // user has no subscription
        if ((user.subscription !== 'active' && user.subscription !== 'trialing') && user.permission !== 'master'){

          if (!path.includes('/account/billing') &&
              !path.includes('/signup/plan') &&
              !path.includes('/account/upgrade') &&
              !path.includes('/account') &&
              !path.includes('/account/profile')){

                return <Navigate to='/account/billing' />

          }
        }

      }

      // user is good
      return props.children;

    }

    // Usuario autenticado pero SIN permiso para esta ruta (ej. admin intentando entrar a /accounting/*):
    if (user.permission === 'accounting') {
      return <Navigate to='/accounting/dashboard' replace />;
    }
    return <Navigate to='/dashboard' replace />;
  }

  // user is not authenticated — guardar ruta para redirigir post-login
  const intendedPath = window.location.pathname + window.location.search;
  if (intendedPath !== '/' && intendedPath !== '/signin') {
    sessionStorage.setItem('redirect_after_login', intendedPath);
  }
  return <Navigate to='/' />;

}
